import { Positions } from "model/Positions";
import { getRosterResult } from "model/Results";
import { Scores } from "model/Scores";

const DEFAULT_POINTS: WDLSeasonPointsRules = {
  win: 1,
  draw: 0,
  loss: 0,
  bonus: undefined,
  dqPoints: undefined,
  finalistPoints: undefined,
  ffPoints: undefined,
};

export type SeasonPoints = number | "Pos" | "Qual" | "Score";

export const isSeasonPoints = (
  value: number | string | undefined
): value is SeasonPoints => {
  if (!value) {
    return false;
  }

  return (
    typeof value === "number" ||
    value === "Pos" ||
    value === "Qual" ||
    value === "Score"
  );
};

const bonusTypes = ["FastestLap", "Other"] as const;
export type BonusType = (typeof bonusTypes)[number];
export const isBonusType = (value: string | undefined): value is BonusType => {
  return bonusTypes.includes(value as BonusType);
};

export type SeasonBonusPointsRules = {
  amount: number | undefined;
  dqAmount: number | undefined;
  ffAmount: number | undefined;
  type: BonusType;
};

export type SeasonPointsRulesBase = {
  bonus: SeasonBonusPointsRules | undefined;
  dqPoints: number | undefined;
  finalistPoints: number | undefined;
  ffPoints: number | undefined;
};

export type PositionSeasonPointsRules = SeasonPointsRulesBase & {
  position: SeasonPoints[];
};

export type WDLSeasonPointsRules = SeasonPointsRulesBase & {
  win: number;
  draw: number;
  loss: number;
};

export type SeasonPointsRules =
  | PositionSeasonPointsRules
  | WDLSeasonPointsRules;

export const isPositionSeasonPointsRules = (
  rules: SeasonPointsRules
): rules is PositionSeasonPointsRules => {
  return (rules as PositionSeasonPointsRules).position !== undefined;
};

export type AltSeasonPointsRules = {
  matchIds: string[];
  points: SeasonPointsRules;
};

export const getRosterSeasonPoints = (
  rosterId: string,
  pointRules: SeasonPointsRules | undefined,
  positions: Positions,
  scores: Scores,
  isFinalist: (score: number) => boolean,
  numWinners: number
) => {
  if (!pointRules) {
    return getTeamWDLSeasonPoints(
      rosterId,
      DEFAULT_POINTS,
      positions,
      numWinners
    );
  }

  const rosterPoints = isPositionSeasonPointsRules(pointRules)
    ? getTeamPositionSeasonPoints(rosterId, pointRules, positions)
    : getTeamWDLSeasonPoints(rosterId, pointRules, positions, numWinners);

  if (rosterPoints === "Score") {
    const teamScore = scores[rosterId];
    if (pointRules.finalistPoints && isFinalist(teamScore)) {
      return pointRules.finalistPoints;
    }

    return teamScore;
  }

  if (rosterPoints === "Pos") {
    return positions[rosterId];
  }

  return rosterPoints;
};

export const getTeamPositionSeasonPoints = (
  rosterId: string,
  points: PositionSeasonPointsRules,
  positions: Positions
) => {
  const teamPosition = positions[rosterId];
  if (!teamPosition) {
    return 0;
  }

  if (teamPosition > points.position.length) {
    return points.position.at(-1) ?? 0;
  }

  return points.position[teamPosition - 1] ?? 0;
};

export const getTeamWDLSeasonPoints = (
  rosterId: string,
  points: WDLSeasonPointsRules,
  positions: Positions,
  numWinners: number
) => {
  const teamResult = getRosterResult(rosterId, positions, numWinners);
  switch (teamResult) {
    case "W":
      return points.win;
    case "D":
      return points.draw;
    case "L":
      return points.loss;
    default:
      return 0;
  }
};

export const getBonusAmount = (
  points: SeasonBonusPointsRules,
  forfeited: boolean,
  disqualified: boolean
) => {
  if (disqualified && points.dqAmount !== undefined) {
    return points.dqAmount;
  }

  if (forfeited && points.ffAmount !== undefined) {
    return points.ffAmount;
  }

  return points.amount;
};
